import { createPointsController } from './ui.js';
import { initMap, drawAreas, drawMarkers, drawPostcodes, fitToBounds } from './map.js';
import { geocode } from './api.js';
import { computeIsochrone } from './isochrone-chain.js';
import { circleArea, unionAll, difference, filterPostcodes, bbox } from './compute.js';
import { loadPostcodes, missingCountries } from './postcodes.js';
import { postcodesToCsv, downloadCsv } from './csv.js';

const $ = (s) => document.querySelector(s);

const mapCtx = initMap(document.getElementById('map'));

const includes = createPointsController({
  listEl: document.querySelector('#includes .items'),
  kind: 'include'
});
const excludes = createPointsController({
  listEl: document.querySelector('#excludes .items'),
  kind: 'exclude'
});

// Seed with one include to start.
includes.add();

// Wire "+ add" buttons.
document.querySelectorAll('.add').forEach((btn) => {
  btn.addEventListener('click', () => {
    if (btn.dataset.list === 'includes') includes.add();
    else excludes.add();
  });
});

let lastMatched = [];

$('#compute').addEventListener('click', run);
$('#download').addEventListener('click', () => {
  if (!lastMatched.length) return;
  downloadCsv('postcodes.csv', postcodesToCsv(lastMatched));
});
$('#copy').addEventListener('click', async () => {
  if (!lastMatched.length) return;
  const codes = [...new Set(lastMatched.map((f) => f.properties?.plz).filter(Boolean))].sort();
  const text = codes.join(', ');
  const btn = $('#copy');
  const original = btn.innerHTML;
  try {
    await navigator.clipboard.writeText(text);
    btn.innerHTML = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l5 5L20 7"/></svg> Copied ' + codes.length;
    btn.classList.add('ok');
    setTimeout(() => { btn.innerHTML = original; btn.classList.remove('ok'); }, 1600);
  } catch (e) {
    btn.textContent = 'Copy failed';
    setTimeout(() => { btn.innerHTML = original; }, 1600);
  }
});

function setStatus(msg, kind = 'muted') {
  const el = $('#status');
  el.textContent = msg || '';
  el.className = `status ${kind}`;
}

function selectedCountries() {
  return [...document.querySelectorAll('input[name="country"]:checked')].map((el) => el.value);
}

async function resolvePoints(points, countries) {
  const out = [];
  for (const p of points) {
    if (!p.address) continue;
    p.setBusy(true);
    try {
      const r = await geocode(p.address, countries);
      p.setResolved(r);
      out.push({ ...p, lat: r.lat, lon: r.lon, label: r.label });
    } catch (e) {
      p.setError(e.message);
    } finally {
      p.setBusy(false);
    }
  }
  return out;
}

async function buildArea(p, onStatus) {
  if (p.mode === 'radius') {
    return circleArea(p.lon, p.lat, Number(p.value) || 0);
  }
  const mins = Math.max(1, Math.min(180, Number(p.value) || 0));
  return computeIsochrone({
    lon: p.lon, lat: p.lat, profile: p.profile, minutes: mins, onStatus
  });
}

async function run() {
  const countries = selectedCountries();
  if (!countries.length) {
    setStatus('Pick at least one country.');
    return;
  }
  const missing = missingCountries(countries);
  if (missing.length) {
    setStatus(`Missing postcode data for: ${missing.join(', ')}`);
    return;
  }

  const inc = includes.getAll().filter((p) => p.address);
  const exc = excludes.getAll().filter((p) => p.address);
  if (!inc.length) {
    setStatus('Add at least one include point.');
    return;
  }

  $('#compute').disabled = true;
  $('#download').disabled = true;
  setStatus('Geocoding…');

  try {
    const resolvedInc = await resolvePoints(inc, countries);
    const resolvedExc = await resolvePoints(exc, countries);
    drawMarkers(mapCtx, [
      ...resolvedInc.map((p) => ({ ...p, kind: 'include' })),
      ...resolvedExc.map((p) => ({ ...p, kind: 'exclude' }))
    ]);

    if (!resolvedInc.length) throw new Error('No include point resolved.');

    setStatus('Building travel areas…');
    const incAreas = [];
    for (let i = 0; i < resolvedInc.length; i++) {
      const p = resolvedInc[i];
      incAreas.push(await buildArea(p, (msg) =>
        setStatus(`Include ${i + 1}/${resolvedInc.length}: ${msg}`)));
    }

    const excAreas = [];
    for (let i = 0; i < resolvedExc.length; i++) {
      const p = resolvedExc[i];
      excAreas.push(await buildArea(p, (msg) =>
        setStatus(`Exclude ${i + 1}/${resolvedExc.length}: ${msg}`)));
    }

    const incValid = incAreas.filter(Boolean);
    const excValid = excAreas.filter(Boolean);
    console.log('[compute] include areas:', incValid.length, '/', incAreas.length);
    console.log('[compute] exclude areas:', excValid.length, '/', excAreas.length);

    const incUnion = unionAll(incValid);
    const excUnion = unionAll(excValid);
    const target = difference(incUnion, excUnion);
    console.log('[compute] inc union:', incUnion?.geometry?.type);
    console.log('[compute] exc union:', excUnion?.geometry?.type);
    console.log('[compute] target (inc - exc):', target?.geometry?.type,
                'rings:', target?.geometry?.coordinates?.length);

    drawAreas(mapCtx, incValid, excValid);
    const bb = bbox(incUnion);
    if (bb) fitToBounds(mapCtx, bb);

    setStatus(`Loading postcodes (${countries.join(', ')})…`);
    const pcs = await loadPostcodes(countries);

    setStatus(`Matching ${pcs.length.toLocaleString()} postcodes…`);
    const mode = document.querySelector('input[name="match-mode"]:checked')?.value || 'centroid';
    const inIncludeOnly = filterPostcodes(incUnion, pcs, mode);
    // Exclusion always uses intersects: any postcode touched by an exclusion
    // zone is dropped, regardless of the selected match mode.
    const excluded = excUnion
      ? new Set(filterPostcodes(excUnion, inIncludeOnly, 'intersects'))
      : new Set();
    const matched = inIncludeOnly.filter((f) => !excluded.has(f));
    const excludedCount = excluded.size;
    console.log('[compute] in include:', inIncludeOnly.length,
                '  excluded:', excludedCount,
                '  final:', matched.length);
    lastMatched = matched;

    drawPostcodes(mapCtx, matched);

    const byCountry = matched.reduce((acc, f) => {
      const c = f.properties.country || '?';
      acc[c] = (acc[c] || 0) + 1;
      return acc;
    }, {});
    const summary = Object.entries(byCountry).map(([c, n]) => `${c}: ${n.toLocaleString()}`).join(' · ');
    const excNote = excValid.length
      ? ` · ${excludedCount.toLocaleString()} excluded by ${excValid.length} exclusion${excValid.length > 1 ? 's' : ''}`
      : '';
    $('#summary').innerHTML =
      `<strong>${matched.length.toLocaleString()}</strong> postcodes${summary ? ` — ${summary}` : ''}${excNote}.`;
    setStatus('Done.', 'ok');
    const hasResults = matched.length > 0;
    $('#download').disabled = !hasResults;
    $('#copy').disabled = !hasResults;
  } catch (e) {
    console.error(e);
    setStatus(e.message || String(e), 'err');
  } finally {
    $('#compute').disabled = false;
  }
}
